import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { DayOfWeek, Shop, ShopWorkTime } from '@/api/backend/contract/Shop';
import { UiKitButton } from '@/ui-kit/button';
import { utilFormatPhoneNumber } from '@/util/format-phone-number';
import { utilPluralize } from '@/util/plularize';

import { styles } from './UiShopPointInfo.styles';

export interface UiShopPointInfoProps {
  point: Shop;
  activePointId: string | null;
  onPointConfirm: (pointId: string) => void;
  onSelectPoint: (point: Shop | null) => void;
  hideConfirmButton?: boolean;
  isFreeShipping?: boolean;
  isVisibleStockBtn?: boolean;
  isScroll?: boolean;
  isDrawer?: boolean;
  type?: 'simple' | 'pdp';
  onLinkClick?: (params: { label: string; url: string }) => void;
}

export function UiShopPointInfo({
  point,
  activePointId,
  onPointConfirm,
  onSelectPoint,
  hideConfirmButton,
  isVisibleStockBtn = false,
  isScroll,
  isDrawer = false,
  type,
  onLinkClick
}: UiShopPointInfoProps) {
  const t = useTranslations();
  const tDays = useTranslations('daysOfWeek');

  const slots = styles({ type });

  const pluralizePieces = utilPluralize({
    one: t('pluralize.pieces.one'),
    few: t('pluralize.pieces.few'),
    many: t('pluralize.pieces.many')
  });

  if (!point.properties) {
    return;
  }

  const catalogShopId = point.properties.shopUuid.trim();

  return (
    <>
      <div className={slots.pointTitle()}>{point.properties.title}</div>
      <div className={slots.pointAddress()}>{point.properties.street}</div>
      <div className={slots.pointHeading()}>
        <div className={slots.pointValue({ isPhone: true })}>
          <Link href={`tel:${point.properties.phone.replace(/\D/g, '')}`}>
            {utilFormatPhoneNumber(point.properties.phone)}
          </Link>
        </div>

        {type === 'simple' && (isDrawer || isScroll) && activePointId === point.id && (
          <>
            {point.properties.shopName && (
              <div className={slots.pointValue()}>{point.properties.shopName}</div>
            )}
            {point.properties.image && (
              <div className={slots.imageWrapper()}>
                <Image
                  className={slots.image()}
                  width={340}
                  height={218}
                  src={point.properties.image}
                  alt={point.properties.title}
                />
              </div>
            )}
          </>
        )}

        <div className={slots.pointValue()}>
          {Array.isArray(point.properties.openingHours) &&
            point.properties.openingHours.length > 0 &&
            (typeof point.properties.openingHours[0] === 'string'
              ? point.properties.openingHours.map((hour, index) =>
                  typeof hour === 'string' ? <div key={index}>{hour}</div> : null
                )
              : (point.properties.openingHours as ShopWorkTime[]).map((wt) => (
                  <div key={wt.id}>
                    <span>{tDays(wt.day as DayOfWeek)}:</span>{' '}
                    <span>
                      {wt.startTime} – {wt.endTime}
                    </span>
                  </div>
                )))}
        </div>
      </div>

      {type === 'pdp' && (
        <div className={slots.stockInfo()}>
          5 {pluralizePieces(5)} {t('inStock')}
        </div>
      )}

      {!isDrawer &&
        type === 'simple' &&
        ((isScroll && activePointId !== point.id) || !isScroll) && (
          <div
            role="button"
            onClick={() => onSelectPoint(point)}
            className={slots.btnMore()}
          >
            {t('Read more')}
          </div>
        )}

      {type === 'simple' && (isDrawer || isScroll) && activePointId === point.id && (
        <>
          <div className={slots.pointHeading()}>
            {point.properties.metro && (
              <div className={slots.pointValue()}>{point.properties.metro}</div>
            )}

            {point.properties.auto && (
              <div className={slots.pointValue()}>{point.properties.auto}</div>
            )}
          </div>
        </>
      )}

      {type === 'simple' && isScroll && activePointId === point.id && (
        <div
          role="button"
          onClick={() => onSelectPoint(null)}
          className={slots.btnMore()}
        >
          {t('Hide')}
        </div>
      )}

      {!isVisibleStockBtn && activePointId === point.id && (
        <>
          {!hideConfirmButton && (
            <div className={slots.pointConfirm()}>
              <UiKitButton
                className={slots.pointConfirmBtn()}
                onClick={() => onPointConfirm(point.id)}
              >
                {t('Book now')}
              </UiKitButton>
            </div>
          )}
        </>
      )}

      {isVisibleStockBtn &&
        Boolean(catalogShopId) &&
        activePointId === point.id &&
        (isDrawer || isScroll) && (
          <div className={slots.pointConfirm()}>
            <UiKitButton
              as="link"
              href={`/catalog/root/?f=shop=${catalogShopId}`}
              className={slots.pointStockBtn()}
              onClick={() => {
                const label = t('Jewelry in stock');
                const url = `/catalog/root/?f=shop=${catalogShopId}`;
                onLinkClick?.({ label, url });
              }}
            >
              {t('Jewelry in stock')}
            </UiKitButton>
          </div>
        )}
    </>
  );
}
